<script setup lang="ts">
// Sierra's .stepper: −, the quantity (typeable), and +. `step`, `min`, `max` and
// `decimals` bound and round it. It emits on every change; debounce in the caller if
// a change is expensive. `label` names it for screen readers.
import { computed } from 'vue';

const props = withDefaults(defineProps<{ modelValue: number; label: string; step?: number; min?: number; max?: number; decimals?: number }>(), {
  step: 1,
  min: Number.NEGATIVE_INFINITY,
  max: Number.POSITIVE_INFINITY,
  decimals: 0,
});
const emit = defineEmits<{ 'update:modelValue': [value: number] }>();

const fit = (value: number): number => {
  const factor = 10 ** props.decimals;
  return Math.min(props.max, Math.max(props.min, Math.round(value * factor) / factor));
};
const shown = computed(() => props.modelValue.toFixed(props.decimals));
const set = (value: number): void => {
  const next = fit(value);
  if (next !== props.modelValue) emit('update:modelValue', next);
};
function onInput(event: Event): void {
  const input = event.target as HTMLInputElement;
  const parsed = Number(input.value.replace(',', '.'));
  if (Number.isFinite(parsed) && input.value.trim() !== '') set(parsed);
  input.value = fit(Number.isFinite(parsed) ? parsed : props.modelValue).toFixed(props.decimals);
}
</script>

<template>
  <div class="itc-stepper" role="group" :aria-label="label">
    <button type="button" class="itc-stepper__btn" :aria-label="`Decrease ${label}`" :disabled="modelValue <= min" @click="set(modelValue - step)">−</button>
    <input class="itc-stepper__q num" inputmode="decimal" :value="shown" :aria-label="label" @change="onInput" />
    <button type="button" class="itc-stepper__btn" :aria-label="`Increase ${label}`" :disabled="modelValue >= max" @click="set(modelValue + step)">+</button>
  </div>
</template>

<style scoped>
.itc-stepper {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.itc-stepper__btn {
  min-width: var(--itc-tap);
  min-height: var(--itc-tap);
  border: 1px solid var(--itc-line-2);
  border-radius: var(--itc-r-control);
  background: var(--itc-panel-2);
  color: var(--itc-fg);
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}
.itc-stepper__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.itc-stepper__q {
  width: 72px;
  min-height: var(--itc-tap);
  border: 1px solid var(--itc-line-2);
  border-radius: var(--itc-r-input);
  background: var(--itc-panel-2);
  color: var(--itc-fg);
  font-size: 18px;
  text-align: center;
}
</style>
